// src/lib/features/featureFlags.ts
export type PlanTier = 'CORE' | 'CATALYST' | 'ENTERPRISE';

export type FeatureKey =
  | 'technicianPassports'
  | 'advancedAnalytics'
  | 'jobWeightEditor'
  | 'optiAssignTuning'
  | 'autonomousOptimizer'
  | 'provisionalAddOn'
  | 'optimizerUndo'
  | 'pensieveLearning'
  | 'optiQueue'
  | 'learningCenter'
  // OptiDVI (addon-gated)
  | 'optiDvi'
  | 'learningCenterAdvanced'
  // OptiAssets (addon-gated)
  | 'optiAssets'
  // Phase 2: Granular Learning Center flags
  | 'learningCenter_catalogAdmin'
  | 'learningCenter_ticketHelp'
  | 'learningCenter_intelligence'
  | 'learningCenter_effectiveness'
  | 'learningCenter_requirements'
  // Diagnostic Intelligence (Catalyst+)
  | 'diagnosticSmartAssignWeight'
  // Role Intelligence (Catalyst+) - Advisor & Manager Passports
  | 'advisorPassports'
  | 'managerPassports'
  // Batch 18-E12: tiny manager-facing "Verify Training" UI on the
  // technician detail page. Default OFF — NOT added to CORE_PRESET or
  // CATALYST_PRESET in this batch. Staged rollout is internal-admin /
  // staging only until the surface graduates to Catalyst. Backend
  // authority is the verify-training-completion Edge Function +
  // verify_training_completion RPC (18-E11); the flag only gates the
  // UI surface.
  | 'managerTrainingVerification'
  | 'roleIntelligence'
  | 'roleTrainingRecommendations'
  | 'roleSelfView'
  | 'roleSmartAssignInfluence'
  // V-F-1g: consolidated Catalyst+ flag for Passport advanced
  // intelligence sections (Performance DNA, Time Signature, Future
  // Insights, Coaching Notes, Role Tracks). Single flag rather than
  // per-component flags per V-F-1e schema decision.
  | 'passportAdvancedIntelligence'
  // Diagnostic Guidance (Catalyst+)
  | 'diagnosticGuidanceEnabled'
  | 'diagnosticStepTrainingEnabled'
  | 'pensieveDiagnosticLearningEnabled'
  // Diagnostic AI Assistance (DIAGNOSTIC FLOWS ONLY)
  | 'diagAiEnabled'           // Master toggle
  | 'diagAiExplainEnabled'    // Phase 1: Explanatory Assistant (no tier restriction - flows already gated)
  | 'diagAiInsightEnabled'    // Phase 2: Path Insight (Catalyst+)
  | 'diagAiPersonalizedEnabled' // Phase 3: Personalized Style (Catalyst+)
  | 'diagAiRecommendEnabled'  // Phase 4: Advanced Considerations (Enterprise only)
  | 'diagAiConfidenceScoringEnabled' // Adaptive phase control
  | 'diagAiDowngradeEnabled'  // Auto-downgrade on struggle
  // OptiDVI Phase 6: Network Intelligence
  | 'dviNetworkIntelligence'  // Enterprise only - network signals dashboard
  | 'dviBenchmarking'         // Enterprise only - peer comparison
  | 'dviDriftDetection'       // Catalyst+ - pattern drift alerts
  | 'dviIndustryContext'      // All tiers (opt-in) - AI industry context
  // OptiDVI Phase 7: Explainability & Auditability
  | 'dviExplainability'       // Catalyst+ - explanation panels & confidence breakdown
  | 'dviAuditLedger'          // Enterprise only - full decision audit trail
  | 'dviEvidencePackets'      // Enterprise only - one-click justification packets
  // OptiDVI Phase 13: Vision/OCR Learning System
  | 'ui_dviLearning'          // OPTIDVI addon - confirm-finding workflow + learning panels
  // Workflow Inspection (informational only -- real gate is shop_inspection_capabilities table)
  | 'inspectionCore'          // All tiers
  | 'inspectionAutoGrade'     // Catalyst+ (informational)
  | 'inspectionAutoNotes'     // Catalyst+ (informational)
  // Book/Chapter Hierarchy (MVP 1)
  // V-F-1f: gates the Books/Chapters tab inside /training-catalog only.
  // Since V-F-1d wrapped /training-catalog in <InternalAdminRoute>, the
  // sole consumer is reachable only by internal admins. Removed from
  // CORE_PRESET in V-F-1f (functionally dead for Core users; matches
  // the original "default false" intent of this comment).
  | 'trainingBookChapters'
  // OptiBay Connect (explicit opt-in, not tier-gated)
  | 'optibayConnect'
  // Beta mode (confidentiality overlay)
  | 'betaMode'
  // OptiBay Parts Financial Infrastructure — Phase 1 (default OFF, not in any preset)
  | 'partsFinancialEngine'
  | 'partsTaxLookup'
  | 'partsFeeRules'
  | 'partsBrowserAssistant'
  // OptiBay Parts — Phase 2 Internal Parts Line Infrastructure (default OFF, not in any preset)
  | 'partsLineItems'
  | 'partsPricing'
  | 'partsStatuses'
  // OptiBay Parts — Phase 3 OptiGuard Pricing Matrix (default OFF, not in any preset)
  | 'partsPricingMatrix'
  | 'partsPricingOptiGuard'
  | 'partsPricingInsights'
  // OptiBay Inventory — Phase 9 Foundation entitlement (default ON for all tiers)
  | 'inventoryEnabled'
  // OptiBay Labor & Financial Engine — Phase 10 R1 (default OFF, internal-admin opt-in)
  | 'labor_engine_enabled'
  // Ticket Lifecycle v2 — slice-1 stubs (default OFF in every preset).
  // See docs/lifecycle/TICKET_LIFECYCLE_V2_SPEC.md §6. These keys
  // reserve the future surface; no consumer reads them in slice 1.
  | 'trainingQuizMvp'
  | 'custodyHandoff'
  | 'advisorTimingAnalytics'
  | 'customerLifecycleAnalytics'
  | 'fohIntakeCapture'
  | 'fohIntakeOcr'
  // Slice 4F — Advanced advisor workflow event capture (future Catalyst
  // surface). Distinct from `advisorTimingAnalytics`, which continues
  // to gate the existing custody-driven capture (foh_accept →
  // ticket_accepted, kick_to_boh → kicked_back_to_tech) and the
  // AdvisorActivityCard. This flag gates *advanced* capture sites —
  // estimate / quote / customer approval / parts / invoice events —
  // which are not part of OptiBay Core beta. Default OFF in every
  // preset. Both flags must be ON for an advanced emit to fire.
  | 'advisorAdvancedActionCapture'
  // Slice 5D-5 — Implicit advisor takeover. When ON (in combination
  // with `advisorAdvancedActionCapture` + `advisorTimingAnalytics`),
  // a FOH-capable user who performs an advisor-authoritative action
  // on a ticket already held by a *different* FOH advisor
  // automatically becomes the new holder via a `reassign_within_foh`
  // custody transition. Allowed trigger action kinds are constrained
  // to a small closed set (`invoice_sent`, `customer_approval_received`
  // today). View/open/read events NEVER trigger takeover. Default OFF
  // in every preset.
  | 'advisorImplicitTakeover'
  // CORE-BN — Front-of-House Attention Capacity. Gates the shop-level
  // front-of-house saturation estimate and the surface that shows it.
  // Display-only and advisory: the capability never blocks intake,
  // refuses a customer, or reassigns a person (CORE-BN "Human
  // Authority"), so the flag governs visibility, not behaviour. Granted
  // by CORE_PRESET — front-of-house attention is a Core capability, and
  // its surface degrades to a stated absence when there is no basis to
  // estimate, so an entitled shop with no usable data simply sees
  // nothing rather than a fabricated figure.
  | 'fohAttentionCapacity'
  // OCIS AI Intake — Phase 1, Slice 1 (V-OCIS-Intake-1). Master
  // toggle for the intake / voice-to-ticket workflow. Default OFF,
  // intentionally NOT included in any tier preset or
  // PRESET_MANAGED_FEATURE_KEYS — internal-admin opt-in only until
  // the PII-scrubber exception design is settled and the feature
  // ships beyond text-only MVP.
  | 'aiIntakeEnabled'
  // Ticket Lifecycle v2 — Slice 5D-11 Batch 1. Display-only flag
  // that opts a shop's Dayboard TicketCards into rendering the
  // new "advisor-aware bay occupancy" block:
  //   - Tech OptiTime
  //   - Advisor Bay Hold
  //   - Bay Occupancy Time
  //   - Bay Finish
  // Default OFF in every preset. Flag-off behavior must be byte-
  // identical to today: no new hooks fire, no new network calls,
  // no new layout. Batch 2 will wire scheduling/cascade to
  // optionally consume this estimate — that is intentionally
  // gated by a separate batch.
  | 'advisorBayOccupancyOnDayboard'
  // Ticket Lifecycle v2 — Slice 5D-13 Batch 3. SCHEDULING-influence
  // flag. When ON (via the 'BAY_OCCUPANCY_AFFECTS_SCHEDULING' addon
  // on shops.addons), reassignCore extends the written
  // `tickets.opti_finish` timestamp by the observed advisor bay
  // hold so cascade/bay-overlap respects the bay actually being
  // occupied while the advisor holds the ticket. Stored
  // `tickets.opti_time` stays tech-only. NEVER shrinks the window.
  // Default OFF in every preset; addon-opt-in required.
  | 'bayOccupancyAffectsScheduling'
  // Ticket Lifecycle v2 — Slice 5D-15 Batch 4. PREDICTED scheduling
  // influence. When ON (via the
  // 'BAY_OCCUPANCY_AFFECTS_SCHEDULING_PREDICTED' addon), AND the
  // base 'BAY_OCCUPANCY_AFFECTS_SCHEDULING' addon is also ON,
  // reassignCore may extend `tickets.opti_finish` by a SERVER-
  // computed predicted advisor bay hold for the current FOH
  // holder. Confidence floor: medium or high only — low confidence
  // is rejected for scheduling even though it may still display.
  // Observed wins over predicted. Default OFF in every preset.
  | 'bayOccupancyAffectsSchedulingPredicted'
  // Adoption Slice 1 — new lean 3-page first-login setup wizard.
  // CORE-preset member. When ON for a freshly-created shop, the
  // post-OnboardShop redirect lands on the FirstLoginJunction +
  // LeanSetupWizard instead of dropping straight into the legacy
  // manager tutorial. Existing shops (created before this flag was
  // on) continue to use the legacy onboarding tutorial.
  | 'firstLoginV2'
  // Adoption Slice 2/3 — Historical Import surface gating. Until the
  // import workflow is end-to-end functional for a given user, the
  // Import Shop History card on FirstLoginJunction must remain
  // hidden. This flag is INTENTIONALLY NOT in any tier preset —
  // internal/dev opt-in only (mirrors the aiIntakeEnabled pattern).
  // Real shops only see the Import card after a separate slice
  // ships the end-to-end import workflow AND this flag is flipped.
  | 'importEnabledForInternal';

export type FeatureFlags = Record<FeatureKey, boolean>;

// ── Preset Architecture ───────────────────────────────────────────────

/** Generate a fully disabled baseline for all feature flags */
export function createEmptyFeatureFlags(): FeatureFlags {
  const keys: FeatureKey[] = [
    'technicianPassports', 'advancedAnalytics', 'jobWeightEditor', 'optiAssignTuning',
    'autonomousOptimizer', 'provisionalAddOn', 'optimizerUndo', 'pensieveLearning',
    'optiQueue', 'learningCenter', 'optiDvi', 'learningCenterAdvanced', 'optiAssets',
    'learningCenter_catalogAdmin', 'learningCenter_ticketHelp', 'learningCenter_intelligence',
    'learningCenter_effectiveness', 'learningCenter_requirements',
    'diagnosticSmartAssignWeight',
    'advisorPassports', 'managerPassports', 'roleIntelligence', 'roleTrainingRecommendations',
    'roleSelfView', 'roleSmartAssignInfluence',
    'passportAdvancedIntelligence',
    'diagnosticGuidanceEnabled', 'diagnosticStepTrainingEnabled', 'pensieveDiagnosticLearningEnabled',
    'diagAiEnabled', 'diagAiExplainEnabled', 'diagAiInsightEnabled', 'diagAiPersonalizedEnabled',
    'diagAiRecommendEnabled', 'diagAiConfidenceScoringEnabled', 'diagAiDowngradeEnabled',
    'dviNetworkIntelligence', 'dviBenchmarking', 'dviDriftDetection', 'dviIndustryContext',
    'dviExplainability', 'dviAuditLedger', 'dviEvidencePackets',
    'ui_dviLearning',
    'inspectionCore', 'inspectionAutoGrade', 'inspectionAutoNotes',
    'trainingBookChapters', 'optibayConnect', 'betaMode',
    'partsFinancialEngine', 'partsTaxLookup', 'partsFeeRules', 'partsBrowserAssistant',
    'partsLineItems', 'partsPricing', 'partsStatuses',
    'partsPricingMatrix', 'partsPricingOptiGuard', 'partsPricingInsights',
    'inventoryEnabled',
    'labor_engine_enabled',
    // Ticket Lifecycle v2 — slice-1 stubs (no preset includes these).
    'custodyHandoff', 'advisorTimingAnalytics', 'customerLifecycleAnalytics',
    'fohIntakeCapture', 'fohIntakeOcr',
    // Slice 4F — advanced advisor capture (no preset includes this).
    'advisorAdvancedActionCapture',
    // Slice 5D-5 — implicit advisor takeover (no preset includes this).
    'advisorImplicitTakeover',
    // CORE-BN — front-of-house attention capacity (CORE_PRESET grants it).
    'fohAttentionCapacity',
    // OCIS AI Intake — Phase 1, Slice 1 (no preset includes this).
    'aiIntakeEnabled',
    // Slice 5D-11 Batch 1 — advisor-aware bay occupancy display
    // (no preset includes this; default OFF; display-only).
    'advisorBayOccupancyOnDayboard',
    // Slice 5D-13 Batch 3 — bay occupancy scheduling influence
    // (no preset includes this; default OFF; addon-gated).
    'bayOccupancyAffectsScheduling',
    // Slice 5D-15 Batch 4 — predicted advisor bay-hold scheduling
    // influence (no preset includes this; default OFF; requires
    // BOTH the base BAY_OCCUPANCY_AFFECTS_SCHEDULING addon AND the
    // BAY_OCCUPANCY_AFFECTS_SCHEDULING_PREDICTED addon).
    'bayOccupancyAffectsSchedulingPredicted',
    // Adoption Slice 1 — new lean 3-page first-login setup wizard
    // (added to CORE_PRESET below). Default OFF in the baseline; the
    // tier preset application step turns it on for Core+ tiers.
    'firstLoginV2',
    // Adoption Slice 2/3 — Historical Import surface gating
    // (no preset includes this; internal/dev opt-in only).
    'importEnabledForInternal',
    // Training Quiz MVP (Phase Q1) — post-completion knowledge check. OFF in the
    // baseline; the CORE_PRESET turns it ON for Core+ tiers (the quiz backend is
    // live). Pure client gate; a feature-store override can still turn it OFF.
    'trainingQuizMvp',
  ];
  const flags = {} as FeatureFlags;
  for (const k of keys) flags[k] = false;
  return flags;
}

/**
 * CORE PRESET — the locked OptiBay Core entitlement set.
 * These flags are granted when shop.tier === 'CORE'.
 *
 * V-F-1f: removed `trainingBookChapters` (functionally dead for Core —
 * its sole consumer `/training-catalog` is now <InternalAdminRoute>-
 * gated by V-F-1d, so non-admin Core users never reach the gate). The
 * union-type doctrine comment ("Admin UI gate — default false") is
 * now consistent with this preset.
 *
 * Slice 5D-18: added the advisor / bay-occupancy display flags + AI
 * Intake flag so flipping a shop to Core lights up the demo/test
 * surface in one motion. Important layering:
 *
 *   - `advisorTimingAnalytics`, `advisorImplicitTakeover`,
 *     `advisorBayOccupancyOnDayboard`, `aiIntakeEnabled` are pure
 *     CLIENT/UI flags. Adding them here is the entire effect.
 *   - `bayOccupancyAffectsScheduling` and
 *     `bayOccupancyAffectsSchedulingPredicted` are also CLIENT flags
 *     read by board/hooks for display, but the SERVER-side scheduling
 *     influence in `reassignCore` reads `shops.addons` directly (see
 *     the addon block in `getFeatureFlags` below). Setting these true
 *     in the preset DOES NOT bypass the addon gate — the edge
 *     function never reads this preset value.
 *   - `advisorAdvancedActionCapture` is INTENTIONALLY EXCLUDED. It
 *     remains preset-OFF in every tier; advanced advisor capture is
 *     not part of OptiBay Core.
 *
 * Slice 5D-19: rounded out the FOH lifecycle / role visibility / intake
 * shell flags that travel with the Slice 5D-18 advisor & bay-occupancy
 * work. Pure CLIENT entitlement additions — no runtime behavior was
 * changed in this slice.
 *
 *   - `custodyHandoff` — foundational FOH/BOH custody flag; advisor
 *     lifecycle timing and bay occupancy build on top of it.
 *   - `advisorPassports`, `managerPassports` — Core now tracks advisor
 *     lifecycle timing and managers/owners frequently act as
 *     advisors, so their Passport surfaces ship at Core. (Note: these
 *     were already true in CATALYST_PRESET; the spread-from-CORE chain
 *     leaves Catalyst/Enterprise unchanged.)
 *   - `customerLifecycleAnalytics` — customer/advisor delay & approval
 *     lifecycle visibility is part of the Core workflow layer.
 *   - `fohIntakeCapture` — pairs with `aiIntakeEnabled` (added in
 *     Slice 5D-18) to give Core shops the FOH intake shell.
 *   - `fohIntakeOcr` is INTENTIONALLY EXCLUDED — OCR stays separately
 *     gated until verified stable. `advisorAdvancedActionCapture`
 *     stays excluded per Slice 5D-18.
 */
export const CORE_PRESET: Partial<FeatureFlags> = {
  technicianPassports: true,
  roleSelfView: true,
  roleSmartAssignInfluence: true,
  learningCenter: true,
  learningCenterAdvanced: true,
  learningCenter_ticketHelp: true,
  learningCenter_effectiveness: true,
  pensieveLearning: true,
  optibayConnect: true,
  betaMode: true,
  inventoryEnabled: true,
  // Slice 5D-18 — advisor / bay-occupancy client flags + AI Intake.
  // See preset jsdoc above for the client-vs-server layering rules.
  advisorTimingAnalytics: true,
  advisorImplicitTakeover: true,
  advisorBayOccupancyOnDayboard: true,
  bayOccupancyAffectsScheduling: true,
  bayOccupancyAffectsSchedulingPredicted: true,
  aiIntakeEnabled: true,
  // Slice 5D-19 — FOH lifecycle, Passport visibility, and intake shell.
  // Pure client entitlement additions; no behavior change in this slice.
  // `fohIntakeOcr` and `advisorAdvancedActionCapture` remain excluded.
  custodyHandoff: true,
  advisorPassports: true,
  managerPassports: true,
  customerLifecycleAnalytics: true,
  fohIntakeCapture: true,
  // CORE-BN — front-of-house attention capacity. Pure client entitlement:
  // the flag reveals a read-only, advisory shop-level estimate on the FOH
  // queue. Nothing server-side consults it, and no scheduling, intake or
  // promise path is gated by it.
  fohAttentionCapacity: true,
  // Adoption Slice 1 — fresh Core shops use the new lean 3-page
  // setup wizard instead of the legacy manager tutorial autostart.
  firstLoginV2: true,
  // Adoption Slice 2/3 — Historical Import surface gating.
  //
  // K19-5: OFF for Core/beta. The historical-import BACKEND is not shipped —
  // the core CSV functions `upload-import-csv`, `promote-import-run`, and
  // `analyze-import-run` are NOT registered in `supabase/config.toml` (so they
  // are not served/deployed), and `upload-import-csv/index.ts:10` states
  // "NOT yet deployed — see the Slice 2.5 UI plan for go-live". A 2026-05-27
  // change had flipped this ON in CORE_PRESET "after end-to-end validation",
  // but since CORE is the beta tier (provision-beta-org provisions beta shops
  // as CORE), that exposed an Import entry point at /onboard/setup whose Upload
  // would fail (function not found) — a broken beta flow (K18-H4).
  //
  // Six consumer surfaces gate on this flag, so OFF cleanly hides import with
  // no broken buttons or dead routes:
  //   1. FirstLoginJunction — the Import card is conditionally rendered; OFF
  //      collapses the chooser to the single Manual Quick Setup card.
  //   2-5. OnboardSetupImport / OnboardSetupImportPreview /
  //        OnboardSetupImportInferences / OnboardSetupImportWizard — page-level
  //        guards redirect back to /onboard/setup when OFF.
  //   6. HistoricalImportDialog — defense-in-depth gate inside the dialog.
  //
  // The import CODE (pages, dialog, functions, migrations) is preserved for a
  // future Slice 2.5 launch; only the beta-facing visibility is off. Internal/
  // dev can still opt in WITHOUT the preset via the feature-store override
  // (`useFeatureStore.featureFlags`, which wins over the tier preset in
  // useFeatureFlags.ts) — matching the flag's "internal/dev opt-in only" intent.
  // Server-side gating is independent (those edge functions never consult this
  // client flag), so this is purely a UI-visibility change.
  //
  // Re-enable for beta ONLY after the import functions are registered in
  // config.toml + deployed and the flow is validated end-to-end (see K19-5).
  //
  // Core-100 R3 (CORE-V-GAP-001): the first two conditions that engineering owns are met on this branch — the three
  // import functions are registered in config.toml and the chain is validated end to end against a REAL database
  // with the functions served (tests/integration/coreVImportRuntimeProof.test.ts, 12/12: staging only, manager
  // only, cross-tenant refused, provenance on every record, idempotent re-run, no live writes, inferences applied
  // to nothing). The third — DEPLOY (Edge functions + the field-encryption key secret) — must precede shipping this
  // frontend: see docs/core100-experiment/ISOLATED_QA_RUNBOOK.md. Import stays manager/owner-only server-side.
  importEnabledForInternal: true,
  // Training Quiz MVP (Phase Q1) — post-completion knowledge check. Enabled by
  // default for Core now that the quiz backend (tables + RPCs + generation) is
  // live and the end-to-end flow is validated; no /feature-flags session
  // override is needed for normal Core usage. Catalyst/Enterprise inherit it via
  // the spread below. Still a pure CLIENT gate — a feature-store override can
  // turn it OFF locally, and the technician RPC serves approved-only quizzes.
  trainingQuizMvp: true,
};

/**
 * CATALYST PRESET — placeholder. Spreads Core + Catalyst-specific flags.
 * Only partially defined; finalize when Catalyst tier ships.
 */
export const CATALYST_PRESET: Partial<FeatureFlags> = {
  ...CORE_PRESET,
  advancedAnalytics: true,
  jobWeightEditor: true,
  optiAssignTuning: true,
  autonomousOptimizer: true,
  provisionalAddOn: true,
  optimizerUndo: true,
  learningCenter_catalogAdmin: true,
  learningCenter_intelligence: true,
  learningCenter_requirements: true,
  diagnosticSmartAssignWeight: true,
  advisorPassports: true,
  managerPassports: true,
  roleIntelligence: true,
  roleTrainingRecommendations: true,
  diagnosticGuidanceEnabled: true,
  diagAiExplainEnabled: true,
  diagAiInsightEnabled: true,
  diagAiPersonalizedEnabled: true,
  dviDriftDetection: true,
  dviExplainability: true,
  inspectionCore: true,
  inspectionAutoGrade: true,
  inspectionAutoNotes: true,
  // V-F-1g: consolidated Passport advanced intelligence flag —
  // gates Performance DNA / Time Signature / Future Insights /
  // Coaching Notes / Role Tracks sections inside PassportHome and
  // TechnicianPassport. Catalyst+ only; Core-tier shops see the
  // basic Passport sections only.
  passportAdvancedIntelligence: true,
};

/**
 * ENTERPRISE PRESET — placeholder. Spreads Catalyst + Enterprise-specific flags.
 */
export const ENTERPRISE_PRESET: Partial<FeatureFlags> = {
  ...CATALYST_PRESET,
  diagAiRecommendEnabled: true,
  dviNetworkIntelligence: true,
  dviBenchmarking: true,
  dviAuditLedger: true,
  dviEvidencePackets: true,
};

/**
 * Entitlement keys that participate in tier presets.
 * Excludes addon-only keys (optiQueue, optiDvi, optiAssets, DVI addon-gated flags)
 * and keys that are only toggled by specific conditions (diagAi master toggles, etc.)
 */
export const PRESET_MANAGED_FEATURE_KEYS: FeatureKey[] = [
  'technicianPassports', 'advancedAnalytics', 'jobWeightEditor', 'optiAssignTuning',
  'autonomousOptimizer', 'provisionalAddOn', 'optimizerUndo', 'pensieveLearning',
  'learningCenter', 'learningCenterAdvanced',
  'learningCenter_catalogAdmin', 'learningCenter_ticketHelp', 'learningCenter_intelligence',
  'learningCenter_effectiveness', 'learningCenter_requirements',
  'diagnosticSmartAssignWeight',
  'advisorPassports', 'managerPassports', 'roleIntelligence', 'roleTrainingRecommendations',
  'roleSelfView', 'roleSmartAssignInfluence',
  'passportAdvancedIntelligence',
  'diagnosticGuidanceEnabled', 'diagnosticStepTrainingEnabled', 'pensieveDiagnosticLearningEnabled',
  'diagAiEnabled', 'diagAiExplainEnabled', 'diagAiInsightEnabled', 'diagAiPersonalizedEnabled',
  'diagAiRecommendEnabled', 'diagAiConfidenceScoringEnabled', 'diagAiDowngradeEnabled',
  'dviDriftDetection', 'dviExplainability',
  'dviNetworkIntelligence', 'dviBenchmarking', 'dviAuditLedger', 'dviEvidencePackets',
  'inspectionCore', 'inspectionAutoGrade', 'inspectionAutoNotes',
  'trainingBookChapters', 'optibayConnect', 'betaMode',
  'inventoryEnabled',
  // Adoption Slice 1 — first-login wizard (Core+).
  'firstLoginV2',
  // Training Quiz MVP — granted by the Core preset (Core+).
  'trainingQuizMvp',
];

/** Entitlement keys excluded from preset matching (none currently) */
export const ADMIN_ONLY_FEATURE_KEYS: FeatureKey[] = [];

/** Exposure keys excluded from preset matching */
export const ADMIN_ONLY_EXPOSURE_KEYS: ExposureKey[] = ['ui_trainingLearnedMappings'];

/** Get the tier preset for a given plan */
function getTierPreset(plan: PlanTier): Partial<FeatureFlags> {
  switch (plan) {
    case 'ENTERPRISE': return ENTERPRISE_PRESET;
    case 'CATALYST': return CATALYST_PRESET;
    case 'CORE':
    default: return CORE_PRESET;
  }
}

export function getFeatureFlags(
  plan: PlanTier,
  addons: string[] = [],
  overrides: Partial<FeatureFlags> = {}
): FeatureFlags {
  const isCatalystPlus = plan === 'CATALYST' || plan === 'ENTERPRISE';
  const isEnterprise = plan === 'ENTERPRISE';

  // 1. Empty baseline
  const flags = createEmptyFeatureFlags();

  // 2. Apply tier preset
  const preset = getTierPreset(plan);
  Object.assign(flags, preset);

  // 3. Apply additional tier-derived flags not in presets (always-on / conditional)
  // These were true in old logic but not part of the named preset
  flags.learningCenter_catalogAdmin = true;       // Core+Catalyst admin
  flags.learningCenter_ticketHelp = true;          // All tiers
  flags.inspectionCore = true;                     // All tiers
  // V-F-1f: Diagnostic AI Explainer is now Catalyst-only. Pre-V-F-1f
  // this was a forced-true override (`flags.diagAiExplainEnabled = true`)
  // with the comment "Phase 1: no tier restriction — flows already
  // gated". The reasoning was correct (the parent /diagnostic-flows
  // surface is exposure-gated to Catalyst), but per V-F-1c product-
  // owner decision #7 the flag itself should reflect tier policy.
  // CATALYST_PRESET already grants `diagAiExplainEnabled: true` (see
  // line 160), so Catalyst-or-higher tiers retain it via preset
  // application above; Core falls through to `false` from the empty
  // baseline. Both consumer sites (AIExplanationCard.tsx,
  // useDiagnosticAI.ts) already use the canonical `useFeatureFlag`
  // hook and pick up the new tier-conditional value automatically.

  // 4. Apply addon logic (unchanged)
  if (addons.includes('OPTIQUEUE')) flags.optiQueue = true;
  if (addons.includes('OPTIASSETS')) flags.optiAssets = true;
  if (addons.includes('OPTIDVI')) {
    flags.optiDvi = true;
    flags.dviIndustryContext = true;
    flags.ui_dviLearning = true; // Phase 13: default-on for OPTIDVI shops
    if (isCatalystPlus) {
      flags.dviDriftDetection = true;
      flags.dviExplainability = true;
    }
    if (isEnterprise) {
      flags.dviNetworkIntelligence = true;
      flags.dviBenchmarking = true;
      flags.dviAuditLedger = true;
      flags.dviEvidencePackets = true;
    }
  }

  // optibayConnect: Core preset grants it, addon also grants it (OR)
  if (addons.includes('OPTIBAY_CONNECT')) flags.optibayConnect = true;

  // Slice 5D-13 Batch 3 — bayOccupancyAffectsScheduling.
  // Addon-gated server-readable feature flag. The reassignCore edge
  // function reads `shops.addons` directly with the same string
  // literal, so the SERVER-side scheduling influence is gated on the
  // addon regardless of tier.
  //
  // Slice 5D-18 changed the CLIENT layering: CORE_PRESET now sets
  // this flag true so Core shops see the Bay Occupancy display
  // without an addon. The OR below is a no-op for Core (preset
  // already wins) and the meaningful path for non-Core shops that
  // opt in via the addon. The server is unaffected — it never reads
  // this client flag.
  if (addons.includes('BAY_OCCUPANCY_AFFECTS_SCHEDULING')) {
    flags.bayOccupancyAffectsScheduling = true;
  }

  // Slice 5D-15 Batch 4 — bayOccupancyAffectsSchedulingPredicted.
  // STRICT LAYERING (server): predicted scheduling requires the base
  // observed-scheduling addon ALSO present. A shop cannot enable
  // predicted scheduling without observed scheduling — the predicted
  // flag silently stays false if the base addon is missing. This
  // mirrors the server-side `isBayOccupancyPredictedSchedulingEnabled`
  // check.
  //
  // Slice 5D-18: CORE_PRESET sets the CLIENT flag true. The addon
  // gate below still only fires for addon-equipped shops, so the
  // behavior for non-Core / no-addon shops is unchanged. The
  // `reassignCore` edge function continues to require BOTH addons —
  // setting this client flag true via the preset does not enable
  // server-side predicted scheduling without the addons.
  if (
    addons.includes('BAY_OCCUPANCY_AFFECTS_SCHEDULING_PREDICTED') &&
    addons.includes('BAY_OCCUPANCY_AFFECTS_SCHEDULING')
  ) {
    flags.bayOccupancyAffectsSchedulingPredicted = true;
  }

  // 5. Apply overrides
  return { ...flags, ...overrides };
}

/**
 * Check if a shop tier is Catalyst or higher
 */
export function isCatalystTier(tier: string | null | undefined): boolean {
  const t = (tier || '').toUpperCase();
  return t === 'CATALYST' || t === 'ENTERPRISE';
}

// ── Exposure Flags (UI visibility layer) ──────────────────────────────

export type ExposureKey =
  | 'ui_chronicle'
  | 'ui_optiGuard'
  | 'ui_evidenceDiagnostics'
  | 'ui_workflowInspections'
  | 'ui_workflowDiagnostics'
  | 'ui_workflowCustomerComms'
  | 'ui_multiChannelComms'
  | 'ui_voipCallIngestion'
  | 'ui_optiAssets'
  | 'ui_customerInbox'
  | 'ui_phoneIntegration'
  | 'ui_trainingCatalogAdmin'
  | 'ui_trainingProviderAdmin'
  | 'ui_trainingDiscoveryAdmin'
  | 'ui_trainingLearnedMappings'
  | 'ui_advancedAdminTools'
  | 'ui_ecosystemAdmin'
  // OptiBay Parts Financial Infrastructure — Phase 1 exposure (default OFF)
  | 'ui_shopProfileTaxesFees'
  | 'ui_partsFinancialPanels'
  | 'ui_partsBrowserAssistant'
  // OptiBay Parts — Phase 2 exposure (default OFF)
  | 'ui_ticketPartsPanel'
  | 'ui_jobPartsSection'
  // OptiBay Parts — Phase 3 exposure (default OFF)
  | 'ui_shopProfilePricingMatrix'
  | 'ui_partsPricingGraph'
  | 'ui_partsPricingHints'
  // OptiBay Parts & Labor — Phase 4 exposure (default OFF; UI re-home of Jobs)
  | 'ui_partsAndLabor'
  // OptiBay Parts Assist — Phase 5 exposure (default OFF; vendor capture intake)
  | 'ui_partsAssist'
  // OptiBay Parts Suggestions — Phase 6A exposure (default OFF; suggestion chips)
  | 'ui_partsSuggestions'
  // OptiBay Parts Pensieve — Phase 6B exposure (default OFF; "Commonly added" co-occurrence chips)
  | 'ui_pensieveSuggestions'
  // OptiBay Inventory — Phase 9 R2 exposure (default OFF; admin UI gates)
  | 'ui_inventory'
  | 'ui_inventory_adjustments'
  | 'ui_inventory_transactions'
  // OptiBay Labor & Financial Engine — Phase 10 exposure (default OFF)
  | 'ui_labor_pricing'           // R1: Shop Profile Labor Pricing tab
  | 'ui_ticket_financials'       // R4 placeholder — declared now, used later
  | 'ui_technician_pay'          // R3 placeholder — declared now, used later
  // OptiBay Customer Invoicing — Phase 11 exposure (default OFF)
  | 'ui_customer_invoices'      // Customer invoicing + authorization system
  // V-F-1d — conservative Core-beta gating for OptiShop / financial routes
  // (default OFF; the routes /purchasing, /canned-jobs, /payroll-reports
  // are wrapped in <ExposureRouteGate> against these keys so a beta Core
  // shop manager who URL-types these paths sees <NotFound /> instead of
  // an OptiShop-scope page).
  | 'ui_purchasing'             // /purchasing list + detail
  | 'ui_canned_jobs'            // /canned-jobs management
  | 'ui_payroll_reports';       // /payroll-reports

export type ExposureFlags = Record<ExposureKey, boolean>;

/** Generate a fully disabled baseline for all exposure flags */
export function createEmptyExposureFlags(): ExposureFlags {
  const keys: ExposureKey[] = [
    'ui_chronicle', 'ui_optiGuard', 'ui_evidenceDiagnostics', 'ui_workflowInspections',
    'ui_workflowDiagnostics', 'ui_workflowCustomerComms', 'ui_multiChannelComms',
    'ui_voipCallIngestion', 'ui_optiAssets', 'ui_customerInbox', 'ui_phoneIntegration',
    'ui_trainingCatalogAdmin', 'ui_trainingProviderAdmin', 'ui_trainingDiscoveryAdmin',
    'ui_trainingLearnedMappings', 'ui_advancedAdminTools', 'ui_ecosystemAdmin',
    'ui_shopProfileTaxesFees', 'ui_partsFinancialPanels', 'ui_partsBrowserAssistant',
    'ui_ticketPartsPanel', 'ui_jobPartsSection',
    'ui_shopProfilePricingMatrix', 'ui_partsPricingGraph', 'ui_partsPricingHints',
    'ui_partsAndLabor',
    'ui_partsAssist',
    'ui_partsSuggestions',
    'ui_pensieveSuggestions',
    'ui_inventory',
    'ui_inventory_adjustments',
    'ui_inventory_transactions',
    'ui_labor_pricing',
    'ui_ticket_financials',
    'ui_technician_pay',
    'ui_customer_invoices',
    // V-F-1d
    'ui_purchasing',
    'ui_canned_jobs',
    'ui_payroll_reports',
  ];
  const flags = {} as ExposureFlags;
  for (const k of keys) flags[k] = false;
  return flags;
}

/**
 * Core exposure defaults — applied to all tiers as the baseline (every
 * tier inherits Core's UI exposure surface; tier-specific UI exposure
 * additions can be layered on later if needed).
 *
 * Used by `getExposureFlags` to compute the runtime exposure object,
 * and by `isMatchingPreset` to compare exposure state against the
 * canonical Core baseline.
 *
 * V-F-1g: `ui_customerInbox` (OCIS Customer Inbox) flipped to default
 * `true` per product-owner decision. OCIS triage is globally visible
 * to Core+ shops; the `/inbox` route's `<ExposureRouteGate>` wrap
 * remains in place so that overrides can still toggle the page off
 * per-shop if needed (e.g., shops with no SMS pipeline integration).
 */
export const CORE_EXPOSURE_DEFAULTS: Partial<ExposureFlags> = {
  ui_customerInbox: true,
};

/**
 * Compute UI exposure flags for the current shop context.
 *
 * Layer order: empty baseline → CORE_EXPOSURE_DEFAULTS → caller
 * overrides. Tier-specific exposure additions (none today) would
 * apply between CORE_EXPOSURE_DEFAULTS and overrides if introduced.
 */
export function getExposureFlags(
  _plan: PlanTier,
  _addons: string[] = [],
  overrides: Partial<ExposureFlags> = {}
): ExposureFlags {
  const base = createEmptyExposureFlags();
  return { ...base, ...CORE_EXPOSURE_DEFAULTS, ...overrides };
}

/**
 * Compare current flags against a tier preset.
 * Checks BOTH entitlement and exposure flags, excluding admin-only keys from each.
 */
export function isMatchingPreset(
  currentFeatures: FeatureFlags,
  currentExposure: ExposureFlags,
  presetFeatures: Partial<FeatureFlags>,
  presetExposure: Partial<ExposureFlags>,
): boolean {
  // Check entitlement flags (only preset-managed, excluding admin-only)
  for (const key of PRESET_MANAGED_FEATURE_KEYS) {
    if (ADMIN_ONLY_FEATURE_KEYS.includes(key)) continue;
    const expected = presetFeatures[key] ?? false;
    if (currentFeatures[key] !== expected) return false;
  }

  // Check exposure flags (all non-admin exposure keys)
  const allExposureKeys = Object.keys(currentExposure) as ExposureKey[];
  for (const key of allExposureKeys) {
    if (ADMIN_ONLY_EXPOSURE_KEYS.includes(key)) continue;
    const expected = presetExposure[key] ?? false;
    if (currentExposure[key] !== expected) return false;
  }

  return true;
}
